import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import {
  DocumentTextIcon,
  ScaleIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  HomeIcon,
  Cog6ToothIcon,
  MicrophoneIcon,
  ArrowPathIcon,
} from '../icons';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import RegistrarNav from '../components/layout/RegistrarNav';
const avatarImage = '/avatar.jpg';

const registrarNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: HomeIcon, end: true },
  { to: '/sessions', label: 'Sessions', icon: MicrophoneIcon },
  { to: '/transcriptions', label: 'Transcriptions', icon: DocumentTextIcon },
  { to: '/court-cases', label: 'Court Cases', icon: ScaleIcon },
  { to: '/regenerations', label: 'Regenerations', icon: ArrowPathIcon },
];

const MenuIcon = () => (
  <span className="flex flex-col justify-center gap-1 w-5 h-5" aria-hidden="true">
    <span className="block h-0.5 w-full bg-current rounded-full" />
    <span className="block h-0.5 w-full bg-current rounded-full" />
    <span className="block h-0.5 w-full bg-current rounded-full" />
  </span>
);

const RegistrarLayout = () => {
  const { user, logout } = useAuth();
  const { displayRole } = usePermissions();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const profileRef = useRef(null);

  const courtName = user?.court_name || user?.courtName || 'Court Console';
  const userName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    : 'User';

  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleConfirmSignOut = () => {
    setSignOutOpen(false);
    setProfileOpen(false);
    logout();
  };

  return (
    <div className="min-h-screen bg-matdash-body md-app-shell flex flex-col">
      <header className="md-topbar sticky top-0 z-40">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between gap-4 py-3 border-b border-matdash-border">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/dashboard" className="flex items-center gap-2 min-w-0 shrink-0">
                <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
                <div className="min-w-0 hidden sm:block">
                  <p className="text-sm font-bold text-matdash-dark truncate leading-tight">CourtScribe</p>
                  <p className="text-[11px] text-matdash-muted truncate">{courtName}</p>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div
                ref={profileRef}
                className="relative"
                onMouseEnter={() => setProfileOpen(true)}
                onMouseLeave={() => setProfileOpen(false)}
              >
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md-lg px-1.5 py-1 hover:bg-matdash-primary-subtle transition-colors"
                  onClick={() => setProfileOpen((v) => !v)}
                  aria-expanded={profileOpen}
                  aria-haspopup="menu"
                >
                  <img
                    src={avatarImage}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover border border-matdash-border bg-matdash-primary-subtle"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/logo.png';
                    }}
                  />
                  <div className="min-w-0 hidden sm:block text-left">
                    <p className="text-sm font-semibold text-matdash-dark truncate max-w-[140px]">{userName}</p>
                    <p className="text-xs text-matdash-muted capitalize truncate">{displayRole}</p>
                  </div>
                </button>

                {profileOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full pt-1 z-50"
                  >
                    <div className="min-w-[200px] bg-white border border-matdash-border rounded-md-lg shadow-matdash py-1">
                      <Link
                        to="/account-settings"
                        role="menuitem"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-matdash-dark hover:bg-matdash-primary-subtle transition-colors"
                        onClick={() => setProfileOpen(false)}
                      >
                        <Cog6ToothIcon className="h-4 w-4 text-matdash-muted" />
                        Account Settings
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-matdash-danger hover:bg-matdash-danger-subtle transition-colors"
                        onClick={() => {
                          setProfileOpen(false);
                          setSignOutOpen(true);
                        }}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="md:hidden p-2 rounded-md-lg border border-matdash-border text-matdash-muted"
                onClick={() => setMobileNavOpen((v) => !v)}
                aria-label="Toggle navigation"
              >
                {mobileNavOpen ? <XMarkIcon className="h-5 w-5" /> : <MenuIcon />}
              </button>
            </div>
          </div>

          {mobileNavOpen && (
            <div className="md:hidden py-3 border-t border-matdash-border">
              <RegistrarNav items={registrarNavItems} onNavigate={closeMobileNav} />
            </div>
          )}
        </div>
      </header>

      {/*
        pt-0 so the nav bar sits flush against the top bar.
        Content starts immediately under the nav (no large gap).
      */}
      <main className="flex-1 px-4 sm:px-6 pt-0 pb-24 lg:pb-6">
        <div className="hidden md:flex items-center justify-start gap-2 py-2 border-b border-matdash-border bg-white -mx-4 sm:-mx-6 px-4 sm:px-6">
          <RegistrarNav items={registrarNavItems} horizontal />
        </div>
        <div className="pt-4">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-matdash-border bg-white px-4 py-4 text-center text-xs text-matdash-muted">
        <span className="inline-flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-5 w-5 rounded-full" />
          Powered by Snwolley AI
        </span>
      </footer>

      {signOutOpen && (
        <div className="md-modal-overlay">
          <div className="md-modal" role="dialog" aria-modal="true" aria-labelledby="sign-out-title">
            <div className="md-modal-header">
              <h2 id="sign-out-title" className="text-lg font-semibold text-matdash-dark mb-0">
                Sign out
              </h2>
              <button
                type="button"
                onClick={() => setSignOutOpen(false)}
                className="text-matdash-muted hover:text-matdash-dark"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="md-modal-body">
              <p className="text-sm text-matdash-muted mb-0">
                Are you sure you want to sign out of CourtScribe?
              </p>
            </div>
            <div className="md-modal-footer justify-end">
              <button
                type="button"
                onClick={() => setSignOutOpen(false)}
                className="md-btn md-btn-outline md-btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSignOut}
                className="md-btn md-btn-danger md-btn-sm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrarLayout;
